/**
 * Post-enrolment handoff for admin MFA.
 *
 * assertAdmin (functions/src/middleware/admin-callable.ts) requires the ID
 * token's `firebase.sign_in_second_factor` claim, which Firebase only stamps
 * on a sign-in that used the factor. Enrolling does not refresh the current
 * session's token, so the only way to obtain an admin-capable session after
 * enrolment is to sign out and sign in again.
 */
export const MFA_ENROLLED_NOTICE = "mfa-enrolled" as const;

export interface MfaHandoff {
  signOut: () => Promise<void>;
  navigate: (opts: { to: "/login"; search: { notice: typeof MFA_ENROLLED_NOTICE } }) => void;
}

export async function completeMfaEnrolment({ signOut, navigate }: MfaHandoff): Promise<void> {
  await signOut();
  navigate({ to: "/login", search: { notice: MFA_ENROLLED_NOTICE } });
}

export type EnrolView =
  | "checking"
  | "signed-out"
  | "ready"
  | "unverified-email"
  | "reauth"
  | "error"
  | "generating";

export interface EnrolViewInput {
  loading: boolean;
  signedIn: boolean;
  secretReady: boolean;
  error: { code?: string } | null;
  needsReauth: boolean;
}

/**
 * Which card /mfa-enroll shows. Pure, so the precedence is testable. Assumes
 * the route's `beforeLoad` has already sent enrolled accounts away — this is
 * the state of an enrolment in progress, nothing else.
 */
export function enrolView(input: EnrolViewInput): EnrolView {
  const { loading, signedIn, secretReady, error, needsReauth } = input;
  // Nobody signed in: whatever error the previous session left behind is
  // moot, and a card with a live Resend button for "your account" is wrong.
  if (!signedIn) return loading ? "checking" : "signed-out";
  if (secretReady) return "ready";
  if (error?.code === "auth/unverified-email") return "unverified-email";
  if (needsReauth) return "reauth";
  if (error) return "error";
  return "generating";
}
